/**
 * Per-category sub-region inspect view per scope §6.1.5. Atlas Day 2.5 P5.
 *
 * N=0 substrate (Phase 0 #5 verified empty categories table). Lighter
 * content per scope §6.1.5 framing — categories sparse currently;
 * substrate populates at Day 88 CF 64 per-entity overrides extension.
 * Eventual-existence framing per D3 lock (c) Day 1 precedent.
 */

import type { JSX } from 'react';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import { PER_CATEGORY_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function PerCategoryInspect(): JSX.Element {
  const catFields = FIELD_CLASSIFICATIONS.categories;
  const fieldEntries = Object.entries(catFields);

  return (
    <InspectShell
      clusterName="Per-entity configuration"
      subRegionName="Per-category"
      surfaceLine="ISG has not yet configured category taxonomy. Substrate exists; classification + inspect shapes anticipate Day 88 CF 64 per-entity overrides expansion."
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.1.5 (lighter content given sparse current state): all category fields
          read as <strong>judgment</strong> at value-altitude when populated; at current N=0
          substrate, the inspect shape is architectural — slots exist for category taxonomy
          configuration that ships at Day 88 CF 64 expansion.
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>
          All category fields classify as <strong>strategic</strong> per scope §6.1.5 framing
          (category taxonomy is ISG&apos;s structural-position choice, not external fact):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {fieldEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer5Nature}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>Per classification map: is_active = operational; name = ownership. Per-field:</p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {fieldEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> —{' '}
              <span className="font-mono">{c.layer6Authority ?? 'N/A'}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Trace pattern resolves when category substrate populates (Day 88 CF 64). Current
          substrate state: N=0 — no trace events yet. At population,{' '}
          <code>categories.provenance</code> (Day 67 P2.2 7-table coverage) carries per-row
          trace.
        </p>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>
          Curated propagation map (verified as of 2026-05-27; anticipates Day 88 CF 64 substrate
          extension):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {PER_CATEGORY_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Per classification map: clean-reversible across category toggles and identity edits.
          Per-field:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {fieldEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer9Reversibility}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Per scope §6.1.5 L11 placeholder framing: per-category configuration contributes to
          ISG&apos;s position as taxonomic structure; override substrate ships with Day 88
          CF 64 per-entity param overrides expansion. At current N=0 state, contribution is
          architectural (slot exists for category-altitude classification stance).
        </p>
      </LayerSection>
    </InspectShell>
  );
}
